import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { MESES_DO_ANO } from '../format.ts';

/** `AAAA-MM`, ou `''` quando a tela aceita "todos os meses". */
export type ValorDaCompetencia = string;

/** Quantos anos para trás a lista oferece, além do corrente. */
const ANOS_PARA_TRAS = 3;

/**
 * Mês e ano como dois combos, que é como se escolhe uma competência.
 *
 * Dois controles e não um campo `month`: o campo nativo abre um calendário
 * diferente em cada navegador, e aqui a escolha é de uma lista curta e
 * conhecida. Os anos são uma lista fixa, do corrente até três atrás, mais o
 * da competência escolhida quando ela for mais velha — descobrir "em que anos
 * há nota" custaria uma varredura do acervo inteiro para preencher um combo.
 *
 * Com `permitirTodos`, a opção vazia é "todos os meses": é o padrão do log de
 * e-mails, que continua respondendo "mandaram as notas da loja tal?" para
 * quem não sabe de que mês elas eram.
 */
export function SeletorDeCompetencia({
  valor,
  aoMudar,
  permitirTodos = false,
  ajuda,
}: {
  valor: ValorDaCompetencia;
  aoMudar: (competencia: ValorDaCompetencia) => void;
  permitirTodos?: boolean;
  ajuda?: string;
}) {
  const [anoEscolhido = '', mesEscolhido = ''] = valor.split('-');
  const anos = anosOferecidos(anoEscolhido);

  /**
   * Uma competência só existe com as duas pontas. Quando a tela aceita
   * "todos", esvaziar um dos combos é escolher isso; quando não aceita, o
   * valor atual fica — não há estado intermediário a guardar.
   */
  function trocar(ano: string, mes: string) {
    if (ano === '' || mes === '') {
      if (permitirTodos) aoMudar('');
      return;
    }
    aoMudar(`${ano}-${mes}`);
  }

  return (
    <Stack direction="row" spacing={1}>
      <TextField
        select
        label="Mês"
        value={mesEscolhido}
        onChange={(evento) => trocar(anoEscolhido, evento.target.value)}
        sx={{ minWidth: 150 }}
        helperText={ajuda}
      >
        {permitirTodos && <MenuItem value="">Todos os meses</MenuItem>}
        {MESES_DO_ANO.map((nome, indice) => (
          <MenuItem key={nome} value={String(indice + 1).padStart(2, '0')}>
            {nome}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Ano"
        value={anoEscolhido}
        onChange={(evento) => trocar(evento.target.value, mesEscolhido)}
        sx={{ minWidth: 110 }}
      >
        {permitirTodos && <MenuItem value="">Todos</MenuItem>}
        {anos.map((ano) => (
          <MenuItem key={ano} value={ano}>
            {ano}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}

/** Do ano corrente para trás, com o da competência escolhida se for mais velho. */
function anosOferecidos(escolhido: string): readonly string[] {
  const corrente = new Date().getFullYear();
  const anos: string[] = [];
  for (let ano = corrente; ano >= corrente - ANOS_PARA_TRAS; ano -= 1) {
    anos.push(String(ano));
  }

  // Um link antigo ou uma competência guardada podem apontar para antes da
  // janela; sem isto o combo abriria vazio sobre um valor que existe.
  if (escolhido !== '' && !anos.includes(escolhido)) anos.push(escolhido);
  return anos;
}

/** A competência do mês que se está fechando: o anterior ao de hoje. */
export function competenciaAnterior(): string {
  const hoje = new Date();
  const mesPassado = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return `${mesPassado.getFullYear()}-${String(mesPassado.getMonth() + 1).padStart(2, '0')}`;
}

/** As duas pontas da competência como `AAAA-MM-DD`, para os filtros de data. */
export function periodoDaCompetencia(competencia: string): { de: string; ate: string } {
  const [ano = '', mes = ''] = competencia.split('-');
  const ultimoDia = new Date(Number(ano), Number(mes), 0).getDate();

  return {
    de: `${competencia}-01`,
    ate: `${competencia}-${String(ultimoDia).padStart(2, '0')}`,
  };
}
